import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LogoIcon, TrustPilotBox } from 'src/components';
import { SText } from 'src/components/SText';
import { AdvisorCatalogLoader } from 'src/containers/advisorCatalogLoader';
import { ColiWithHistoryPublic } from 'src/features/TrackingColisPublic/components/TrackingColiPublicContent/components/ColiWithHistoryPublic/ColiWithHistoryPublic';
import { currencySymbolMapping } from 'src/helpers';
import { useCanThisShippingCreateDeliveryRequest } from 'src/hooks/useCanThisShippingCreateDeliveryRequest';
import { DeliveryWidgetInfo } from '../../DeliveryWidgetInfo';
import { DeliveryPublicColiInfoDataModel } from '../../delivery-public-colis-info.types';

type Props = {
  testID?: string;
  data: DeliveryPublicColiInfoDataModel;
  code: string;
};

export const DeliveryPublicColiInfoContent: React.FC<Props> = ({
  data: { toAgency, restToPay, currencySymbol: currency, colis, ...rest },
  code,
}) => {
  const canClientCreateDeliveryRequest = useCanThisShippingCreateDeliveryRequest({
    shipping: { colis, toAgency, currencySymbol: currency, restToPay, ...rest },
  });

  const [currentSelected, setCurrentSelected] = useState<string | null>(null);

  const { t } = useTranslation();
  const onSelectHandler = useCallback(
    (selected: string) => {
      if (currentSelected === selected) {
        return setCurrentSelected(null);
      }
      setCurrentSelected(selected);
    },
    [setCurrentSelected, currentSelected],
  );

  const ctaDeliveryToHome = useMemo(() => {
    if (!canClientCreateDeliveryRequest) {
      return null;
    }

    return (
      <div className="flex justify-center items-center">
        <a
          href={`/requestDelivery/${rest.tracker}/${code}`}
          className="text-center w-full md:w-auto px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold text-lg rounded-lg shadow-lg transform transition-transform duration-300 hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-300 inline-block m-auto"
        >
          {t('client-delivery-requests.create-new-request')}
        </a>
      </div>
    );
  }, []);

  return (
    <div className="m-auto md:flex md:justify-center md:items-center min-h-screen min-w-screen">
      <div
        className="md:w-1/2  mx-2 bg-white rounded-t-sm shadow-md py-4 md:px-4 px-2 sm:min-h-screen w-screen min-h-screen"
        style={{ margin: '0 auto' }}
      >
        <LogoIcon className="w-40 md:w-64 my-2 m-auto" />

        <script
          async
          src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6226260594799382"
          crossOrigin="anonymous"
        ></script>
        <ins
          className="adsbygoogle"
          style={{ display: 'block' }}
          data-ad-format="fluid"
          data-ad-layout-key="-if+f+o-ag+hi"
          data-ad-client="ca-pub-6226260594799382"
          data-ad-slot="2269656608"
        ></ins>
        <script>(adsbygoogle = window.adsbygoogle || []).push({});</script>

        <AdvisorCatalogLoader zone="TrackingUnderColis" />

        {ctaDeliveryToHome}

        <DeliveryWidgetInfo currency={currency} toAgency={toAgency} code={code} restToPay={restToPay} />

        <div className="my-5 ">
          {!!colis.length && <SText label="public.your-colis" classNames="text-primary my-1" />}
          <table className="my-4 border-1 border-black border-solid w-full ">
            {colis.map((coli) => {
              return (
                <>
                  <tr key={coli.tracker} className="space-x-2 w-full py-4">
                    <td className="text-left  p-1 text-sm md:text-md">
                      <SText label={coli.tracker} classNames="" />
                    </td>
                    <td className="text-left  p-1 text-sm md:text-md">
                      <SText label={coli?.selectedCatalogPrice?.name ?? coli?.selectedEnum ?? '-'} classNames="" />
                    </td>
                    <td className="text-left  p-1 text-sm md:text-md">
                      <SText
                        label={coli.selectedCatalogPrice ? '' : 'public.mesured'}
                        classNames=""
                        translatingOptions={{ value: Number(coli.measureValue).toFixed(2) }}
                      />
                    </td>
                    <td className="text-left  p-1 text-sm md:text-md">
                      <SText
                        label="public.price"
                        classNames=""
                        translatingOptions={{
                          price: coli.price.toFixed(2),
                          currency: currencySymbolMapping(coli.priceCurrency ?? currency) ?? '€',
                        }}
                      />
                    </td>
                  </tr>
                </>
              );
            })}
          </table>
        </div>

        <AdvisorCatalogLoader zone="TrackingUnderColis" />
        <script
          async
          src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6226260594799382"
          crossOrigin="anonymous"
        ></script>
        <ins
          className="adsbygoogle"
          style={{ display: 'block' }}
          data-ad-format="fluid"
          data-ad-layout-key="-if+f+o-ag+hi"
          data-ad-client="ca-pub-6226260594799382"
          data-ad-slot="2269656608"
        ></ins>
        <script>(adsbygoogle = window.adsbygoogle || []).push({});</script>

        <div>
          {colis.map((coli) => {
            return (
              <ColiWithHistoryPublic
                data={{ coli, toAgency }}
                key={coli.tracker}
                isOpen={true}
                onSelect={onSelectHandler}
              />
            );
          })}

          <AdvisorCatalogLoader zone="TrackingUnderColis" />

          {ctaDeliveryToHome}

          <script
            async
            src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6226260594799382"
            crossOrigin="anonymous"
          ></script>
          <ins
            className="adsbygoogle"
            style={{ display: 'block' }}
            data-ad-format="fluid"
            data-ad-layout-key="-if+f+o-ag+hi"
            data-ad-client="ca-pub-6226260594799382"
            data-ad-slot="2269656608"
          ></ins>
          <script>(adsbygoogle = window.adsbygoogle || []).push({});</script>

          <div className="my-4">
            <TrustPilotBox />
          </div>
        </div>
      </div>
    </div>
  );
};
